import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { styled } from 'styled-components';

import { Button } from '#/components/Button/Button';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { H1 } from '#/components/Text/H1';
import { FlashesContainer } from '#/containers/FlashesContainer';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { orgDashboardManageListingsPath } from '#/modules/orgDashboard/routing/orgDashboard.paths';
import { userDashboardSelectOrgPath } from '#/modules/userDashboard/routing/userDashboard.paths';
import { homePath } from '#/routing/routes';
import { colors, gutterWidth } from '#/theme/theme';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';
import { getLocalStorageJsonItem } from '#/utils/localStorage';
import { hideModal, showModal } from '#/zustand-stores/modalStore';

import { MultiStepFormBaseHeader_Hide_1 } from './MultiStepFormBaseHeader.styled';

const testMode = getLocalStorageJsonItem('testMode') as boolean;

type Props = {
  submitDraft?: () => Promise<void>;
  otherLinks?: ReactNode[];
  edited?: boolean;
  trackingTitle?: string;
  title?: string;
  children?: ReactNode;
  isDraft?: boolean;
  cartDropdownButton?: ReactNode;
};

function CloseButton({
  edited,
  submitDraft,
  isDraft,
}: {
  edited: boolean;
  submitDraft?: () => Promise<void>;
  isDraft?: boolean;
}) {
  const { i18n, locale } = useI18n();
  const { user } = useUser();

  const navigate = useNavigate();

  function handleCloseForm() {
    hideModal();
    const orgCount = user?.orgs?.length ?? 0;

    if (orgCount === 0) {
      void navigate(homePath.with({ lang: locale }));
    } else if (orgCount === 1) {
      const orgId = user?.orgs?.[0]?.id as string;
      void navigate(orgDashboardManageListingsPath.with({ orgId }));
    } else {
      void navigate(userDashboardSelectOrgPath.with({}));
    }
  }

  if (submitDraft && isDraft && edited) {
    const extraButtons = (
      <Button
        type="button"
        variant="danger"
        onClick={() => {
          handleCloseForm();
        }}
        qaId="discard-button"
      >
        {i18n('Continue Without Saving')}
      </Button>
    );

    return (
      <Button
        type="button"
        onClick={() => {
          showModal('CONFIRMATION', {
            qaId: 'multi-step-save-draft-modal',
            type: 'warning',
            title: i18n('Do you want to save your draft?'),
            buttonText: i18n('Save Draft'),
            extraButtons,
            onConfirm: submitDraft,
          });
        }}
        title={i18n('Cancel')}
        qaId="multi-form-close"
        tabIndex={0}
        icon={{ name: 'close' }}
        variant="tertiary-grey"
      />
    );
  }

  return (
    <Button
      type="button"
      onClick={() => {
        if (edited) {
          showModal('CONFIRMATION', {
            qaId: 'multi-step-close-modal',
            type: 'danger',
            title: i18n('Are you sure you want to go back?'),
            buttonText: i18n('Yes, go back'),
            body: i18n('If you go back, your data will not be saved.'),
            onConfirm: () => Promise.resolve(handleCloseForm()),
          });
        } else {
          handleCloseForm();
        }
      }}
      title={i18n('Cancel')}
      qaId="multi-form-close"
      icon={{ name: 'close' }}
      variant="tertiary-grey"
      tabIndex={0}
    />
  );
}

const HeaderRow = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 76px;
  padding: 0 ${gutterWidth / 2}px;
  border-bottom: 1px solid #eaeaea;
`;

export function MultiStepFormBaseHeader({
  submitDraft,
  otherLinks,
  edited,
  trackingTitle,
  title,
  isDraft,
  children,
  cartDropdownButton,
}: Props) {
  const { locale } = useI18n();
  return (
    <div
      style={{
        position: testMode ? 'relative' : 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        background: 'white',
        zIndex: 100,
      }}
    >
      <HeaderRow>
        <Link
          to={homePath.with({ lang: locale })}
          data-qa-id="header-logo-link"
          discover="none"
          onClick={(event) =>
            trackAnchorClicked(
              event,
              `Idealist Logo Header - ${
                title ? `${trackingTitle}` : 'Multi Step Form'
              }`,
            )
          }
        >
          <IdealistLogo withText height={42} />
        </Link>

        {title && (
          <MultiStepFormBaseHeader_Hide_1>
            <H1 styleAs="h3" style={{ margin: 0 }}>
              {title}
            </H1>
          </MultiStepFormBaseHeader_Hide_1>
        )}

        <div style={{ display: 'flex', alignItems: 'center' }}>
          {cartDropdownButton}

          <CloseButton
            submitDraft={submitDraft}
            edited={Boolean(edited)}
            isDraft={isDraft}
          />
        </div>
      </HeaderRow>

      {otherLinks && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            borderBottom: '1px solid #eaeaea',
          }}
        >
          {otherLinks.map((link, index) => (
            <div
              key={index}
              style={{
                paddingInline: 32,
                height: 52,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                border:
                  index > 0 ? `1px solid ${colors.selectionGrey}` : undefined,
              }}
            >
              {link}
            </div>
          ))}
        </div>
      )}

      <FlashesContainer />

      {children}
    </div>
  );
}
